/**
 * Autocomplete — React (web) implementation.
 *
 * Mirrors the native composition: the design-system TextInput with a
 * suggestion panel rendered in flow beneath the field. The consuming screen
 * owns querying and debounce; a pick fires `onSelectSuggestion`.
 *
 * Accessibility follows the WAI-ARIA combobox pattern (ADR-025 Rules 1/4):
 * the input carries `role="combobox"` with `aria-expanded` announcing panel
 * appearance, the panel is a listbox of options, and Arrow/Enter/Escape drive
 * an `aria-activedescendant` highlight — keyboard users never tab through
 * rows.
 */

import React from 'react';
import { mouseHoverHandlers } from '../../elements/mouseHover.web';
import { TextInput, type WebTextInputProps } from '../TextInput/TextInput.web';
import {
  MAX_RENDERED_SUGGESTIONS,
  FIELD_TO_PANEL_GAP,
  SUGGESTION_LABEL_COLOR,
  SUGGESTION_LABEL_TYPOGRAPHY,
  SUGGESTION_PANEL_BACKGROUND,
  SUGGESTION_PANEL_BORDER_COLOR,
  SUGGESTION_PANEL_BORDER_WIDTH,
  SUGGESTION_PANEL_RADIUS,
  SUGGESTION_ROW_HORIZONTAL_PADDING,
  SUGGESTION_ROW_VERTICAL_PADDING,
  SUGGESTION_SUBLABEL_COLOR,
  SUGGESTION_SUBLABEL_TYPOGRAPHY,
  SUGGESTION_TEXT_GAP,
  type AutocompleteSuggestion,
} from './Autocomplete';

export interface WebAutocompleteProps {
  actionId?: string;
  label: string;
  value?: string;
  placeholder?: string;
  helperText?: string;
  errorText?: string;
  hideLabel?: boolean;
  disabled?: boolean;
  /** A fetch is in flight; accepted for state parity (no spinner today). */
  loading?: boolean;
  suggestions?: AutocompleteSuggestion[];
  /** Consumer-localized accessible name for the suggestion listbox; defaults
   *  to the field label (the design system bakes no copy). */
  suggestionsLabel?: string;
  /** Shown in place of the rows when the field has text, nothing is loading
   *  and there are no suggestions (e.g. an `AutocompleteEmpty`). Announced
   *  through a polite live region. */
  emptyState?: React.ReactNode;
  onChangeText?: (text: string) => void;
  onSelectSuggestion?: (suggestion: AutocompleteSuggestion) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  autoFocus?: boolean;
  /** Browser autofill purpose forwarded to the inner input. Defaults to `off`:
   *  a suggestion-driven combobox owns its own dropdown, so the browser's list
   *  must not race the suggestion panel. Pass a purpose (e.g. `address-level1`)
   *  when browser autofill is wanted. */
  autoComplete?: WebTextInputProps['autoComplete'];
  /** Form-field `name` forwarded to the inner input. */
  name?: string;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function Autocomplete({
  actionId = 'ui.autocomplete.select',
  label,
  value,
  placeholder,
  helperText,
  errorText,
  hideLabel,
  disabled,
  loading,
  suggestions = [],
  suggestionsLabel,
  emptyState,
  onChangeText,
  onSelectSuggestion,
  onBlur,
  onFocus,
  autoFocus,
  autoComplete = 'off',
  name,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebAutocompleteProps) {
  // Local render bound — the panel is unvirtualized by design (≤10 rows), so
  // the cap must hold here, not only in the server contract.
  const visibleSuggestions = suggestions.slice(0, MAX_RENDERED_SUGGESTIONS);
  const showPanel = visibleSuggestions.length > 0 && !disabled;
  const showEmptyState =
    !showPanel && !disabled && !loading && Boolean(value?.trim()) && Boolean(emptyState);

  const reactId = React.useId();
  const listboxId = `${testId ?? 'autocomplete'}-${reactId}-listbox`;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const [activeIndex, setActiveIndex] = React.useState(-1);
  // Pointer hover reuses the Active look; it never moves the keyboard highlight.
  const [hoveredIndex, setHoveredIndex] = React.useState(-1);
  // The highlight indexes into the CURRENT suggestion set; a new set (fresh
  // results, or the panel emptying) must drop any carried-over highlight.
  const suggestionSetKey = visibleSuggestions.map((suggestion) => suggestion.id).join('\u0000');
  React.useEffect(() => {
    setActiveIndex(-1);
    setHoveredIndex(-1);
  }, [suggestionSetKey]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (!showPanel) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % visibleSuggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? visibleSuggestions.length - 1 : index - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      const picked = visibleSuggestions[activeIndex];
      if (picked) onSelectSuggestion?.(picked);
      setActiveIndex(-1);
    } else if (event.key === 'Escape') {
      setActiveIndex(-1);
    }
  };

  return (
    <div data-testid={testId}>
      <TextInput
        label={label}
        value={value}
        placeholder={placeholder}
        helperText={helperText}
        errorText={errorText}
        hideLabel={hideLabel}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        name={name}
        onChangeText={onChangeText}
        onBlur={onBlur}
        onFocus={onFocus}
        combobox={{
          expanded: showPanel,
          controls: listboxId,
          activeDescendant: activeIndex >= 0 ? optionId(activeIndex) : undefined,
          onKeyDown: handleKeyDown,
        }}
        data-testid={testId ? `${testId}-input` : undefined}
        aria-label={ariaLabel ?? label}
      />
      {showPanel ? (
        <ul
          role="listbox"
          id={listboxId}
          aria-label={suggestionsLabel ?? label}
          data-testid={testId ? `${testId}-suggestions` : undefined}
          style={{
            listStyle: 'none',
            margin: `${FIELD_TO_PANEL_GAP}px 0 0`,
            padding: 0,
            background: SUGGESTION_PANEL_BACKGROUND,
            border: `${SUGGESTION_PANEL_BORDER_WIDTH}px solid ${SUGGESTION_PANEL_BORDER_COLOR}`,
            borderRadius: SUGGESTION_PANEL_RADIUS,
            overflow: 'hidden',
          }}
        >
          {visibleSuggestions.map((suggestion, index) => (
            // An option must have no interactive descendants, so the row
            // itself takes the click; keyboard activation routes through the
            // combobox input's activedescendant + Enter.
            <li
              key={suggestion.id}
              role="option"
              id={optionId(index)}
              aria-selected={index === activeIndex}
              {...mouseHoverHandlers((over) => setHoveredIndex(over ? index : -1))}
              data-analytics-action={actionId}
              onClick={() => onSelectSuggestion?.(suggestion)}
              data-testid={testId ? `${testId}-suggestion-${index}` : undefined}
              style={{
                cursor: 'pointer',
                padding: `${SUGGESTION_ROW_VERTICAL_PADDING}px ${SUGGESTION_ROW_HORIZONTAL_PADDING}px`,
                background:
                  index === activeIndex || index === hoveredIndex
                    ? SUGGESTION_PANEL_BORDER_COLOR
                    : 'transparent',
                borderTop:
                  index > 0
                    ? `${SUGGESTION_PANEL_BORDER_WIDTH}px solid ${SUGGESTION_PANEL_BORDER_COLOR}`
                    : undefined,
              }}
            >
              <span
                style={{
                  display: 'block',
                  fontFamily: SUGGESTION_LABEL_TYPOGRAPHY.fontFamily,
                  fontSize: SUGGESTION_LABEL_TYPOGRAPHY.fontSize,
                  lineHeight: `${SUGGESTION_LABEL_TYPOGRAPHY.lineHeight}px`,
                  color: SUGGESTION_LABEL_COLOR,
                }}
              >
                {suggestion.label}
              </span>
              {suggestion.sublabel ? (
                <span
                  style={{
                    display: 'block',
                    marginTop: SUGGESTION_TEXT_GAP,
                    fontFamily: SUGGESTION_SUBLABEL_TYPOGRAPHY.fontFamily,
                    fontSize: SUGGESTION_SUBLABEL_TYPOGRAPHY.fontSize,
                    lineHeight: `${SUGGESTION_SUBLABEL_TYPOGRAPHY.lineHeight}px`,
                    color: SUGGESTION_SUBLABEL_COLOR,
                  }}
                >
                  {suggestion.sublabel}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {/* Always mounted so screen readers are already watching it when the
          empty state appears. */}
      <div aria-live="polite" data-testid={testId ? `${testId}-empty` : undefined}>
        {showEmptyState ? <div style={{ marginTop: FIELD_TO_PANEL_GAP }}>{emptyState}</div> : null}
      </div>
    </div>
  );
}
